import { safeWhipEndpoint } from "../whip.mjs";
import type { StreamSummary } from "../types/streamSummary";
import type { StreamStateValue } from "../types/streamState";

export type StreamOutputInfo = {
  id: string;
  name: string;
  url: string;
  description?: string;
  type?: string;
  enabled?: boolean;
};

export type WhipStats = { bytesAvailable: boolean; videoMode?: string; videoCodec?: string; encoder?: string; httpStatus?: number; dataChannelsAccepted: boolean; klvChannels: number; klvSent: number; klvDropped: number };

export type SeiStats = { state: string; forwarded: number; dropped: number; lastMessageTime?: string };

export type StreamOutputStatsSnapshot = {
  clientCount?: number;
  deliveryFormat?: string;
  sei?: SeiStats;
  whip?: WhipStats;
  lastError?: string;
  playbackUrl?: string;
  metadataUrl?: string;
  segmentFormat?: string;
  publicationState?: string;
  bitrate: number;
  bytesSent: number;
  bytesTimedOut?: boolean;
  lastBytesChangedAt?: number;
};

export const resolveOutputState = (
  rawState: StreamStateValue | null | undefined,
  stats?: StreamOutputStatsSnapshot | null,
  isStreamRunning = true,
  enabled = true,
): StreamStateValue => {
  // Output statistics can persist briefly after a pipeline is stopped. They
  // must not make a stopped stream's outputs appear healthy.
  if (enabled && stats?.deliveryFormat && stats.publicationState === "serving") return "online";
  if (!isStreamRunning || !enabled) {
    return "offline";
  }
  if (stats?.sei) {
    if (stats.sei.state === "error" || stats.sei.state === "unsupported") return "error";
    if (stats.sei.state === "waiting") return "connecting";
    return stats.sei.state === "forwarding" ? "online" : "offline";
  }
  if (stats?.publicationState === "error") return "error";
  if (stats?.whip && stats.publicationState === "running") return "online";
  if (stats?.publicationState === "stopped" || stats?.publicationState === "disconnected") return "offline";
  if (stats?.publicationState === "starting" || stats?.publicationState === "connecting") return "connecting";
  if (rawState === "error" || rawState === "connecting") {
    return rawState;
  }

  // Connection-aware backend state is authoritative. In particular, a file
  // source may still be producing bytes while an SRT target is disconnected.
  if (rawState === "offline") {
    return rawState;
  }

  if (stats?.bytesTimedOut === true) {
    return "offline";
  }

  if (typeof stats?.bitrate === "number" && Number.isFinite(stats.bitrate) && stats.bitrate > 0) {
    return "online";
  }

  return "offline";
};

export const isRecastOnlyMode = (mode?: string | null): boolean => {
  const normalized = (mode ?? "").trim().toLowerCase();
  return normalized === "recast";
};

export const isRecastMode = (mode?: string | null): boolean => {
  const normalized = (mode ?? "").trim().toLowerCase();
  return normalized === "recast" || normalized === "playandrecast" || normalized === "recastandplay";
};

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
};

const pickFirstString = (record: Record<string, unknown>, keys: string[]): string => {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
  }
  return "";
};

const pickFirstNumber = (record: Record<string, unknown>, keys: string[]): number | null => {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "number" && Number.isFinite(value)) {
      return value;
    }
    if (typeof value === "string" && value.trim()) {
      const parsed = Number(value);
      if (Number.isFinite(parsed)) {
        return parsed;
      }
    }
  }
  return null;
};

const formatRawPcapOutput = (config: Record<string, unknown>): string => {
  const mode = pickFirstString(config, ["mode", "Mode"]).toLowerCase();
  const interfaceId = pickFirstString(config, ["interfaceId", "InterfaceID"]) || "unselected interface";
  if (mode === "original") {
    return `Original frames via ${interfaceId}`;
  }
  const destinationIp = pickFirstString(config, ["destinationIp", "DestinationIP"]) || "unconfigured destination";
  const destinationPort = pickFirstNumber(config, ["destinationPort", "DestinationPort"]);
  return `${destinationIp}${destinationPort && destinationPort > 0 ? `:${Math.round(destinationPort)}` : ""} via ${interfaceId}`;
};

export const extractRecastOutputs = (targets?: StreamSummary["targets"]): StreamOutputInfo[] => {
  if (!Array.isArray(targets)) {
    return [];
  }

  const outputs: StreamOutputInfo[] = [];
  targets.forEach((target, targetIdx) => {
    if (!target) {
      return;
    }
    const targetId =
      typeof target.id === "string" && target.id.trim() ? target.id.trim() : `target-${targetIdx}`;
    const engines = Array.isArray(target.engines) ? target.engines : [];
    engines.forEach((engine) => {
      if (!engine) {
        return;
      }
      const config = isRecord(engine.config) ? engine.config : {};
      const rawType = typeof engine.type === "string" ? engine.type.trim() : "";
      const isRawPcap = rawType.toLowerCase() === "rawpcap";
      const configuredUrl = pickFirstString(config, ["url", "URL"]);
      const isRTSP = rawType.toLowerCase() === "rtsp";
      const isLLHLS = rawType.toLowerCase() === "llhls";
      if (!configuredUrl && !isRawPcap && !isLLHLS && !isRTSP) {
        return;
      }
      const url = isRTSP ? pickFirstString(config, ["path"]) || "Automatic RTSP playback URL" : rawType.toLowerCase() === "whip" ? safeWhipEndpoint(configuredUrl) : isLLHLS ? pickFirstString(config, ["hlsPlaybackUrl"]) || "Automatic playback URL" : isRawPcap ? formatRawPcapOutput(config) : configuredUrl;
      const name =
        pickFirstString(config, ["name", "Name"]) ||
        (typeof engine.id === "string" && engine.id.trim()
          ? engine.id.trim()
          : targetId);
      const description = pickFirstString(config, ["description", "Description"]);
      const inferredType = (() => {
        if (rawType) {
          return rawType.toLowerCase();
        }
        if (!url) {
          return "";
        }
        const match = url.match(/^([a-z0-9+.-]+):\/\//i);
        return match ? match[1].toLowerCase() : "";
      })();
      const engineId = typeof engine.id === "string" ? engine.id.trim() : "";
      const uniqueId = engineId
        ? engineId.toLowerCase() === targetId.toLowerCase()
          ? targetId
          : `${targetId}-${engineId}`
        : targetId;
      outputs.push({
        id: uniqueId,
        name,
        url,
        description,
        enabled: engine.enabled !== false,
        type: inferredType || undefined
      });
    });
  });
  return outputs;
};

export const hasRTSPPublication = (stats?: Map<string, StreamOutputStatsSnapshot>): boolean => Boolean(stats && [...stats.values()].some(s => s.deliveryFormat && s.publicationState !== "stopped"));
